import { useCallback } from 'react';
import { useBlocker } from 'react-router-dom';

interface UseCancelCheckProps {
  totalChanges: number;
}

interface UseCancelCheckReturn {
  isCancelCheck: boolean;
  message: string;
  handleStay: () => void;
  handleLeave: () => void;
}

/**
 * Hook to warn users when navigating away from a page with unsaved changes
 * Uses react-router's useBlocker to intercept navigation attempts
 *
 * @param totalChanges - Number of unsaved changes
 * @returns Object with prompt state and handlers
 */
export const useCancelCheck = ({ totalChanges = 0 }: UseCancelCheckProps): UseCancelCheckReturn => {
  const message = `You have ${totalChanges === 1 ? '1 unsaved change' : totalChanges + ' unsaved changes'}. Please review and action.`;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) =>
      totalChanges > 0 &&
      historyAction !== 'POP' && // browser back/forward not supported by useBlocker on react-router v6
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.hash !== nextLocation.hash ||
        currentLocation.search !== nextLocation.search),
  );

  const isCancelCheck = blocker.state === 'blocked';

  const handleStay = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const handleLeave = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  return {
    isCancelCheck,
    message,
    handleStay,
    handleLeave,
  };
};
